import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listGroups, searchAll } from '../utils/secureFunctions';
import { getProfileUrl } from '../utils/urls';

interface SearchResult {
  id: string;
  type: 'pulse' | 'post' | 'testimony' | 'user' | 'group';
  title: string;
  subtitle: string;
  imageURL?: string;
  username?: string;
}

type FilterType = 'all' | 'people' | 'posts' | 'videos' | 'testimonies' | 'groups';

const RECENT_SEARCHES_KEY = 'belivah_recent_searches';

function normalizeTerm(term: string) {
  return term.trim();
}

export default function SearchBar() {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [allResults, setAllResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const hasQuery = searchTerm.trim().length >= 1;

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (raw) setRecentSearches(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  const rememberSearchTerm = (term: string) => {
    const normalized = normalizeTerm(term);
    if (!normalized) return;

    setRecentSearches((prev) => {
      const next = [
        normalized,
        ...prev.filter((entry) => entry.toLowerCase() !== normalized.toLowerCase()),
      ].slice(0, 6);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const performSearch = async (termRaw: string) => {
    const term = normalizeTerm(termRaw);
    if (!term) {
      setAllResults([]);
      return;
    }

    setSearching(true);
    try {
      const [res, groupsRes] = await Promise.all([
        searchAll(term, 50),
        listGroups({ limit: 50 }).catch(() => ({ ok: true as const, groups: [] as any[] })),
      ]);

      const aggregated: SearchResult[] = [];

      (res.pulses || []).forEach((p: any) => {
        aggregated.push({
          id: p.id,
          type: 'pulse',
          title: p.caption || 'Pulse Video',
          subtitle: `by ${p.userName || 'User'}`,
        });
      });

      (res.posts || []).forEach((p: any) => {
        const content = String(p.content || '');
        aggregated.push({
          id: p.id,
          type: 'post',
          title: content.slice(0, 60) + (content.length > 60 ? '…' : ''),
          subtitle: `by ${p.userName || 'User'}`,
          imageURL: p.imageURL || undefined,
        });
      });

      (res.testimonies || []).forEach((t: any) => {
        aggregated.push({
          id: t.id,
          type: 'testimony',
          title: t.title || 'Testimony',
          subtitle: `by ${t.userName || 'User'}`,
          imageURL: t.imageURL || undefined,
        });
      });

      (res.users || []).forEach((u: any) => {
        aggregated.push({
          id: u.id,
          type: 'user',
          title: u.name || 'User',
          subtitle: u.username ? `@${u.username}` : 'User',
          imageURL: u.photoURL || undefined,
          username: u.username || undefined,
        });
      });

      const lower = term.toLowerCase();
      (groupsRes.groups || []).forEach((g: any) => {
        const name = String(g.name || '');
        const desc = String(g.description || '');
        if (!name.toLowerCase().includes(lower) && !desc.toLowerCase().includes(lower)) return;
        aggregated.push({
          id: g.id,
          type: 'group',
          title: name || 'Group',
          subtitle: `${g.memberCount || 0} members`,
          imageURL: g.coverImage || undefined,
        });
      });

      setAllResults(aggregated);
    } catch (error) {
      console.error('Search failed:', error);
      setAllResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const term = normalizeTerm(searchTerm);
    if (!term) {
      setAllResults([]);
      return;
    }

    const handle = window.setTimeout(() => {
      performSearch(term);
    }, 250);

    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const filteredResults = useMemo(() => {
    const base = allResults;
    if (activeFilter === 'all') return base;
    if (activeFilter === 'people') return base.filter((r) => r.type === 'user');
    if (activeFilter === 'posts') return base.filter((r) => r.type === 'post');
    if (activeFilter === 'videos') return base.filter((r) => r.type === 'pulse');
    if (activeFilter === 'testimonies') return base.filter((r) => r.type === 'testimony');
    if (activeFilter === 'groups') return base.filter((r) => r.type === 'group');
    return base;
  }, [activeFilter, allResults]);

  const visibleResults = filteredResults.slice(0, 20);

  const handleResultClick = (result: SearchResult) => {
    rememberSearchTerm(searchTerm || result.title || '');
    setShowResults(false);
    setSearchTerm('');
    setActiveFilter('all');

    switch (result.type) {
      case 'pulse':
        navigate('/pulse');
        break;
      case 'post':
        navigate('/home');
        break;
      case 'testimony':
        navigate('/testimony');
        break;
      case 'user':
        navigate(getProfileUrl({ id: result.id, username: result.username }));
        break;
      case 'group':
        navigate(`/groups/${result.id}`);
        break;
    }
  };

  const handleRecentClick = (term: string) => {
    setSearchTerm(term);
    setShowResults(true);
    setActiveFilter('all');
    rememberSearchTerm(term);
    performSearch(term);
  };

  return (
    <div style={styles.wrap}>
      <input
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowResults(true);
        }}
        onFocus={() => setShowResults(true)}
        placeholder="Search Belivah"
        style={styles.input}
      />

      {showResults && (
        <div style={styles.dropdown}>
          <div style={styles.filtersRow}>
            {(['all', 'people', 'posts', 'videos', 'testimonies', 'groups'] as FilterType[]).map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                style={{
                  ...styles.filterTab,
                  ...(activeFilter === filter ? styles.filterTabActive : {}),
                }}
              >
                {filter}
              </button>
            ))}
          </div>

          {!hasQuery && recentSearches.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Recent</div>
              <div style={styles.chips}>
                {recentSearches.map((t) => (
                  <button key={t} style={styles.chip} onClick={() => handleRecentClick(t)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!hasQuery && (
            <div style={styles.helperText}>Start typing to search users, posts, pulse videos, testimonies, and groups.</div>
          )}

          {hasQuery && searching && <div style={styles.helperText}>Searching…</div>}

          {hasQuery && !searching && visibleResults.length === 0 && (
            <div style={styles.helperText}>No results.</div>
          )}

          {hasQuery && !searching && visibleResults.length > 0 && (
            <div style={styles.resultsList}>
              {visibleResults.map((r) => (
                <div key={`${r.type}-${r.id}`} style={styles.resultItem} onClick={() => handleResultClick(r)}>
                  {r.imageURL ? <img src={r.imageURL} alt="" style={styles.resultImage} /> : <div style={styles.resultPlaceholder} />}
                  <div style={styles.resultText}>
                    <div style={styles.resultTitle}>{r.title}</div>
                    <div style={styles.resultSubtitle}>{r.subtitle}</div>
                  </div>
                  <div style={styles.resultType}>{r.type}</div>
                </div>
              ))}
            </div>
          )}

          <div style={styles.footerRow}>
            <button
              style={styles.closeBtn}
              onClick={() => {
                setShowResults(false);
                setActiveFilter('all');
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    position: 'relative',
    width: '100%',
    maxWidth: 520,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.12)',
    outline: 'none',
    background: '#fff',
  },
  dropdown: {
    position: 'absolute',
    top: 44,
    left: 0,
    right: 0,
    zIndex: 50,
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
  filtersRow: {
    display: 'flex',
    gap: 8,
    padding: 10,
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    overflowX: 'auto',
  },
  filterTab: {
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filterTabActive: {
    background: '#072B2C',
    borderColor: '#072B2C',
    color: '#fff',
  },
  helperText: {
    padding: 12,
    fontSize: 13,
    color: 'rgba(0,0,0,0.65)',
  },
  resultsList: {
    maxHeight: 420,
    overflowY: 'auto',
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    cursor: 'pointer',
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  resultImage: {
    width: 36,
    height: 36,
    borderRadius: 8,
    objectFit: 'cover',
    background: '#eee',
  },
  resultPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: 'rgba(0,0,0,0.06)',
  },
  resultText: {
    flex: 1,
    minWidth: 0,
  },
  resultTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#072B2C',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resultSubtitle: {
    fontSize: 12,
    color: 'rgba(0,0,0,0.6)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resultType: {
    fontSize: 11,
    color: 'rgba(0,0,0,0.45)',
    textTransform: 'uppercase',
  },
  section: {
    padding: 12,
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#072B2C',
    marginBottom: 8,
  },
  chips: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  chip: {
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    cursor: 'pointer',
  },
  footerRow: {
    padding: 10,
    borderTop: '1px solid rgba(0,0,0,0.08)',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeBtn: {
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#fff',
    borderRadius: 10,
    padding: '6px 10px',
    fontSize: 12,
    cursor: 'pointer',
  },
};


